/**
 * @description Array 프로토타입 확장 메서드와 배열 관련 유틸리티 함수를 제공합니다.
 */
declare global {
  interface Array<T> {
    /**
     * 배열의 특정 위치에 요소들을 삽입합니다.
     * @param elementsToInsert - 삽입할 요소 배열
     * @param position - 삽입 위치 (기본값: 배열 끝)
     * @returns 수정된 원본 배열
     */
    insert: (elementsToInsert: Array<T>, position?: number) => Array<T>;

    /**
     * 배열 길이가 지정된 길이가 되도록 앞에 값을 채웁니다.
     * String.prototype.padStart와 유사한 동작입니다.
     * @param len - 목표 배열 길이
     * @param value - 채울 값
     * @returns 패딩된 새 배열
     */
    padStart: (len: number, value?: T) => Array<T>;

    /**
     * 배열에서 falsy 값들을 제거합니다.
     * null, undefined, 0, '', NaN 등이 제거됩니다.
     * @returns falsy 값이 제거된 새 배열
     */
    removeEmpty: () => Array<Exclude<Exclude<Exclude<T, undefined>, null>, ''>>;

    /**
     * 중복 요소를 제거하고 고유한 요소만 남깁니다.
     * @returns 중복이 제거된 새 배열
     */
    unique: () => Array<T>;

    /**
     * 원본 배열의 요소들을 무작위로 섞습니다 (in-place).
     * Fisher-Yates 알고리즘을 사용합니다.
     */
    shuffleInPlace: () => void;

    /**
     * 요소들이 무작위로 섞인 새 배열을 반환합니다.
     * 원본 배열은 변경되지 않습니다.
     * @returns 섞인 새 배열
     */
    shuffle: () => Array<T>;

    /**
     * 배열을 n개 그룹으로 균등 분할한다. 그룹 간 크기 차이는 최대 1.
     * @throws n <= 0이면 RangeError
     */
    splitEvenly: (n: number) => Array<Array<T>>;
  }
}

/**
 * 배열의 특정 위치에 요소들을 삽입합니다.
 * @param arrToInsert - 원본 배열에 추가할 배열
 * @param position - 새 요소들이 추가될 위치 (생략 시 맨 뒤에 추가)
 * @returns 수정된 원본 배열
 *
 * @example
 * ```ts
 * const arr = [1, 2, 5];
 * arr.insert([3, 4], 2); // [1, 2, 3, 4, 5]
 * ```
 */
Array.prototype.insert = function <T>(elementsToInsert: T[], position?: number) {
  let pos = position;
  if (pos === undefined || pos === null || pos < 0) {
    pos = this.length;
  }
  this.splice(pos, 0, ...elementsToInsert);
  return this;
};

/**
 * 배열 길이가 len이 되도록 앞 부분에 value를 추가합니다.
 *
 * @example
 * ```ts
 * [1, 2, 3].padStart(5, 0); // [0, 0, 1, 2, 3]
 * ```
 */
Array.prototype.padStart = function <T>(len: number, value?: T) {
  if (len <= this.length) {
    return this;
  }
  return Array(len - this.length)
    .fill(value)
    .concat(this);
};

/**
 * falsy 값을 제거하는 타입 가드 필터 함수입니다.
 * Array.prototype.filter와 함께 사용하여 타입 안전하게 falsy 값을 제거합니다.
 *
 * @param item - 검사할 아이템
 * @returns item이 truthy이면 true
 *
 * @example
 * ```ts
 * const arr = [1, null, 2, undefined, 3, '', 0];
 * const filtered = arr.filter(removeEmptyFilter); // [1, 2, 3]
 * ```
 */
export const removeEmptyFilter = function <T>(item: T): item is Exclude<Exclude<Exclude<T, undefined>, null>, ''> {
  return !!item;
};

/**
 * 배열에서 falsy 값들을 제거합니다.
 *
 * @example
 * ```ts
 * [1, null, 2, undefined, 3].removeEmpty(); // [1, 2, 3]
 * ```
 */
Array.prototype.removeEmpty = function <T>(): Array<Exclude<Exclude<Exclude<T, undefined>, null>, ''>> {
  return this.filter(removeEmptyFilter);
};

/**
 * 중복 요소를 제거합니다.
 *
 * @example
 * ```ts
 * [1, 2, 2, 3, 3, 3].unique(); // [1, 2, 3]
 * ```
 */
Array.prototype.unique = function <T>(): Array<T> {
  return Array.from(new Set(this));
};

/**
 * 원본 배열을 무작위로 섞습니다 (메모리 효율적).
 * Fisher-Yates 알고리즘 사용 - O(n) 시간 복잡도.
 *
 * @example
 * ```ts
 * const arr = [1, 2, 3, 4, 5];
 * arr.shuffleInPlace(); // arr이 무작위로 섞임
 * ```
 */
Array.prototype.shuffleInPlace = function (): void {
  for (let i = this.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [this[i], this[j]] = [this[j], this[i]];
  }
};

/**
 * 무작위로 섞인 새 배열을 반환합니다.
 * Fisher-Yates 알고리즘 사용 - O(n) 시간 복잡도.
 *
 * @example
 * ```ts
 * const arr = [1, 2, 3, 4, 5];
 * const shuffled = arr.shuffle(); // 새 배열 반환, arr은 변경되지 않음
 * ```
 */
Array.prototype.shuffle = function <T>(): T[] {
  const result = [...this];
  result.shuffleInPlace();
  return result;
};

/**
 * 배열을 n개 그룹으로 균등 분할한다. 그룹 간 크기 차이는 최대 1.
 * @throws n <= 0이면 RangeError
 */
Array.prototype.splitEvenly = function <T>(n: number): T[][] {
  return splitEvenly(this, n);
};

/**
 * Object.entries의 타입 안전 버전입니다.
 * 키 타입이 보존되고, 값은 non-undefined로 보장됩니다.
 *
 * @typeParam T - 객체 타입
 * @typeParam K - 키 타입
 * @param obj - 대상 객체
 * @returns [키, 값] 튜플 배열
 *
 * @example
 * ```ts
 * const obj = { a: 1, b: 2 };
 * entries(obj).forEach(([key, value]) => {
 *   // key: 'a' | 'b', value: number (undefined 아님)
 * });
 * ```
 */
export function entries<T extends Record<string, unknown>, K extends Extract<keyof T, string>>(
  obj: T,
): Array<[K, NonNullable<T[K]>]> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.entries(obj) as Array<[K, NonNullable<T[K]>]>;
}

/**
 * Object.keys의 타입 안전 버전입니다.
 * 반환되는 키 배열의 타입이 정확하게 추론됩니다.
 *
 * @typeParam T - 객체 타입
 * @typeParam K - 키 타입
 * @param obj - 대상 객체
 * @returns 키 배열
 *
 * @example
 * ```ts
 * const obj = { a: 1, b: 2 };
 * const objKeys = keys(obj); // ('a' | 'b')[]
 * ```
 */
export function keys<T extends Record<string, unknown>, K extends Extract<keyof T, string>>(obj: T): K[] {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.keys(obj) as K[];
}

/**
 * 배열을 n개 그룹으로 균등 분할한다. 그룹 간 크기 차이는 최대 1.
 * @throws n <= 0이면 RangeError
 */
export function splitEvenly<T>(arr: T[], n: number): T[][] {
  if (n <= 0) throw new RangeError(`splitEvenly: n must be positive, got ${n}`);
  if (arr.length === 0) return [];
  const result: T[][] = [];
  const base = Math.floor(arr.length / n);
  const remainder = arr.length % n;
  let idx = 0;
  for (let i = 0; i < n; i++) {
    const size = base + (i < remainder ? 1 : 0);
    result.push(arr.slice(idx, idx + size));
    idx += size;
  }
  return result;
}

export {};
